import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { STATUS } from 'constants/miscConstants';
import AdminService from 'services/AdminService';

const { SUCCESS, ERROR } = STATUS;

const emptyPage = { invoices: [], totalPages: 0, totalCount: 0 };

export const initialState = {
  loading: false,
  message: '',
  showMessage: false,
  status: SUCCESS,
  data: { companies: [], totalPages: 0, totalCount: 0 },
  filters: { pageNumber: 1, pageSize: 10 },
  details: null,
  detailsLoading: false,
  history: emptyPage,
  historyLoading: false,
  historyFilters: { pageNumber: 1, pageSize: 10 },
  downloadingId: null,
};

export const getInvoiceCompanies = createAsyncThunk('invoices/getInvoiceCompanies', async (data, { rejectWithValue }) => {
  const { queryParams, pageNumber, pageSize } = data;
  try {
    const response = await AdminService.getInvoices(queryParams);
    return { response, pageNumber, pageSize };
  } catch (err) {
    return rejectWithValue(err?.message || 'Something went wrong');
  }
});

export const getInvoiceDetails = createAsyncThunk('invoices/getInvoiceDetails', async (companyId, { rejectWithValue }) => {
  try {
    return await AdminService.getCompanyInvoiceDetails(companyId);
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to load invoice details');
  }
});

export const getInvoiceHistory = createAsyncThunk('invoices/getInvoiceHistory', async (data, { rejectWithValue }) => {
  const { companyId, queryParams, pageNumber, pageSize } = data;
  try {
    const response = await AdminService.getCompanyInvoiceHistory(companyId, queryParams);
    return { response, pageNumber, pageSize };
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to load previous invoices');
  }
});

export const downloadInvoice = createAsyncThunk('invoices/downloadInvoice', async ({ invoiceId }, { rejectWithValue }) => {
  try {
    const blob = await AdminService.downloadInvoice(invoiceId);
    return { invoiceId, blob };
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to download the invoice');
  }
});

const setFailure = (state, action) => {
  state.status = ERROR;
  state.message = action.payload;
  state.showMessage = true;
};

export const invoicesSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    hideMessage: (state) => {
      state.message = '';
      state.status = SUCCESS;
      state.showMessage = false;
    },
    resetInvoiceDetails: (state) => {
      state.details = null;
      state.history = emptyPage;
      state.historyFilters = { pageNumber: 1, pageSize: 10 };
    },
  },
  extraReducers: (builder) => {
    builder
      // List
      .addCase(getInvoiceCompanies.pending, (state) => {
        state.loading = true;
      })
      .addCase(getInvoiceCompanies.fulfilled, (state, action) => {
        const { response, pageNumber, pageSize } = action.payload;
        state.data = response || { companies: [], totalPages: 0, totalCount: 0 };
        state.filters.pageNumber = pageNumber;
        state.filters.pageSize = pageSize;
        state.loading = false;
      })
      .addCase(getInvoiceCompanies.rejected, (state, action) => {
        state.loading = false;
        setFailure(state, action);
      })
      // Details
      .addCase(getInvoiceDetails.pending, (state) => {
        state.detailsLoading = true;
      })
      .addCase(getInvoiceDetails.fulfilled, (state, action) => {
        state.details = action.payload;
        state.detailsLoading = false;
      })
      .addCase(getInvoiceDetails.rejected, (state, action) => {
        state.detailsLoading = false;
        setFailure(state, action);
      })
      // History
      .addCase(getInvoiceHistory.pending, (state) => {
        state.historyLoading = true;
      })
      .addCase(getInvoiceHistory.fulfilled, (state, action) => {
        const { response, pageNumber, pageSize } = action.payload;
        state.history = response || emptyPage;
        state.historyFilters.pageNumber = pageNumber;
        state.historyFilters.pageSize = pageSize;
        state.historyLoading = false;
      })
      .addCase(getInvoiceHistory.rejected, (state, action) => {
        state.historyLoading = false;
        setFailure(state, action);
      })
      // Download
      .addCase(downloadInvoice.pending, (state, action) => {
        state.downloadingId = action.meta.arg?.invoiceId ?? null;
      })
      .addCase(downloadInvoice.fulfilled, (state) => {
        state.downloadingId = null;
      })
      .addCase(downloadInvoice.rejected, (state, action) => {
        state.downloadingId = null;
        setFailure(state, action);
      });
  },
});

export const { hideMessage, resetInvoiceDetails } = invoicesSlice.actions;

export default invoicesSlice.reducer;
